if (!customElements.get('product-description')) {
  const RESIZE_DEBOUNCE = 150;
  const TRANSITION_FALLBACK = 400;
  const FALLBACK_LINE_HEIGHT = 20;
  const CLAMP_HEIGHT_SLACK = 2;
  const ANIMATED_PROPERTY = 'max-height';

  class ProductDescription extends HTMLElement {
    expanded = false;
    collapsedHeight = 0;
    prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    connectedCallback() {
      this.content = this.querySelector('.product-description__content');
      this.toggle = this.querySelector('.product-description__toggle');
      if (!this.content || !this.toggle) return;

      this.lines = parseInt(this.style.getPropertyValue('--pd-lines'), 10) || 3;
      this.expandLabel = this.dataset.expandLabel || 'Read more';
      this.collapseLabel = this.dataset.collapseLabel || 'Read less';

      this.text = (this.content.textContent || '').replace(/\s+/g, ' ').trim();

      this.preview = document.createElement('span');
      this.preview.className = 'product-description__preview';
      this.content.insertBefore(this.preview, this.content.firstChild);

      this.classList.add('pd-ready');
      this.toggle.addEventListener('click', this.onClick);
      window.addEventListener('resize', this.onResize);
      this.render();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      this._clearTransition();
    }

    onResize = () => {
      clearTimeout(this._resizeTimer);
      if (this.expanded) return;
      this._resizeTimer = setTimeout(this.render, RESIZE_DEBOUNCE);
    };

    onClick = () => {
      if (this.classList.contains('is-animating')) return;

      this.expanded = !this.expanded;
      this.toggle.setAttribute('aria-expanded', this.expanded ? 'true' : 'false');
      this.toggle.setAttribute('aria-label', this.expanded ? this.collapseLabel : this.expandLabel);

      if (this.prefersReducedMotion) {
        this.classList.toggle('is-expanded', this.expanded);
        this.render();
        return;
      }

      if (this.expanded) {
        this.animateExpand();
      } else {
        this.animateCollapse();
      }
    };

    render() {
      if (!this.expanded) this.showCollapsed();
    }

    showCollapsed() {
      this.classList.remove('is-clamped');
      const maxHeight = this._measureClampHeight();

      if (this.content.scrollHeight <= maxHeight) {
        this.collapsedHeight = 0;
        return;
      }

      this.classList.add('is-clamped');
      this.preview.textContent = this._truncateToFit(maxHeight) + '…';
      this.collapsedHeight = this.content.offsetHeight;
    }

    animateExpand() {
      const fromHeight = this.content.offsetHeight;

      this.classList.add('is-expanded', 'is-animating');
      const toHeight = this._transitionHeight(fromHeight, this.content.scrollHeight);

      if (fromHeight >= toHeight) {
        this._finishTransition();
        return;
      }
      this._bindTransitionEnd(() => this._finishTransition());
    }

    animateCollapse() {
      const fromHeight = this.content.scrollHeight;
      const toHeight = this.collapsedHeight || fromHeight;

      this.classList.add('is-animating');
      this.classList.remove('is-expanded');
      this._transitionHeight(fromHeight, toHeight);

      if (fromHeight <= toHeight) {
        this._finishTransition();
        this.showCollapsed();
        return;
      }
      this._bindTransitionEnd(() => {
        this._finishTransition();
        this.showCollapsed();
      });
    }

    _measureClampHeight() {
      const probe = document.createElement('span');
      probe.style.cssText = 'display:block;visibility:hidden;height:auto;';
      probe.innerHTML = '&nbsp;';
      this.content.appendChild(probe);
      const lineHeight = probe.clientHeight || FALLBACK_LINE_HEIGHT;
      probe.remove();
      return lineHeight * this.lines + CLAMP_HEIGHT_SLACK;
    }

    _truncateToFit(maxHeight) {
      const words = this.text.split(' ');
      let lo = 0;
      let hi = words.length;
      let best = 0;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        this.preview.textContent = words.slice(0, mid).join(' ') + '…';
        if (this.content.scrollHeight <= maxHeight) {
          best = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }
      return words.slice(0, best).join(' ').replace(/[\s.,;:!?\-]+$/, '');
    }

    _transitionHeight(fromHeight, toHeight) {
      this.content.style.maxHeight = fromHeight + 'px';
      void this.content.offsetHeight;
      this.content.style.maxHeight = toHeight + 'px';
      return toHeight;
    }

    _bindTransitionEnd(callback) {
      this._clearTransition();
      this._onTransitionEnd = (event) => {
        if (event.propertyName !== ANIMATED_PROPERTY) return;
        callback();
      };
      this.content.addEventListener('transitionend', this._onTransitionEnd);
      this._transitionFallback = setTimeout(() => {
        if (this.classList.contains('is-animating')) callback();
      }, TRANSITION_FALLBACK);
    }

    _finishTransition() {
      this.content.style.maxHeight = '';
      this.classList.remove('is-animating');
      this._clearTransition();
    }

    _clearTransition() {
      if (this._onTransitionEnd) {
        this.content.removeEventListener('transitionend', this._onTransitionEnd);
        this._onTransitionEnd = null;
      }
      clearTimeout(this._transitionFallback);
    }
  }

  customElements.define('product-description', ProductDescription);
}
